import { $, el, aboveBotBadge } from './dom.js';
import { Popover, searchField } from './popover.js';
import { ct } from './i18n.js';

/**
 * Role and channel picker, single or multiple.
 *
 * Replaces the native `<select>` and `<select multiple>` elements, which caused
 * four problems reported in use:
 *   • an invisible selection - the native highlight is unreadable on a dark background;
 *   • horizontal scrolling as soon as a role name exceeded the width;
 *   • overlapping labels, the native line height ignoring the case;
 *   • no search, impractical beyond twenty entries.
 *
 * Here: a floating panel, explicit checkboxes, search, clean truncation, and
 * removable chips for the multiple mode.
 */

export interface SelectOption {
  id: string;
  name: string;
  /** Colour swatch (roles). */
  hexColor?: string | null;
  /**
   * A small image drawn before the name — a flag, today.
   *
   * Emoji cannot do this job: Windows draws a flag emoji as the two letters of
   * its country code, which is how the language picker came to read "FR". The
   * file is one this server ships, so every platform draws the same thing.
   */
  iconUrl?: string | null;
  /** Flags a role the bot cannot grant. */
  aboveBot?: boolean;
  /**
   * A channel the bot cannot write in.
   *
   * The counterpart of `aboveBot` for roles, and it exists for the same reason:
   * a setting the bot cannot honour looks exactly like one it can. One server
   * chose two channels the bot could not see, wrote its welcome messages for
   * half an hour, and got an internal error on every preview.
   */
  writable?: boolean;
  /** Prefix displayed: `#` for a channel, `@` for a role. */
  prefix?: string;
  /** Makes the option unselectable. */
  disabled?: boolean;
}

export interface SelectMenuOptions {
  /** Element being replaced; keeps its `name` for serialisation. */
  root: HTMLElement;
  /**
   * Identifier of the `<select>` being replaced, carried onto the trigger.
   *
   * The replacement dropped it, so every `<label for="…">` in the views pointed
   * at nothing: twelve of them across five pages, which cost the fields their
   * accessible name and stopped clicking the label from reaching the control.
   * It goes on the trigger rather than on the host, a `<div>` not being a
   * labelable element - a label pointing at one is just as broken, only more
   * quietly.
   */
  id?: string;
  options: SelectOption[];
  initial: string[];
  multiple: boolean;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  /**
   * Whether "no selection" is one of the answers.
   *
   * A single-choice menu always offered it, and for most fields that is right:
   * "no channel", "no role", "the same channel". For the ones where every
   * answer is a real value — the bot's language, a time zone — it offered a
   * choice the form then refused, and a reader who took it saw their setting
   * rejected for a reason nobody had shown them.
   *
   * Read from the native tag rather than declared twice: a field that allows
   * nothing carries an empty `<option>` saying so, and one that does not, does
   * not.
   */
  clearable: boolean;
  max?: number;
  onChange?: (selected: string[]) => void;
}

export class SelectMenu {
  private selected: string[];
  private readonly byId: Map<string, SelectOption>;
  private readonly trigger: HTMLButtonElement;
  private readonly display: HTMLElement;
  private query = '';

  constructor(private readonly config: SelectMenuOptions) {
    this.byId = new Map(config.options.map((option) => [option.id, option]));
    this.selected = [...new Set(config.initial)].filter(
      (id) => this.byId.has(id) || !config.multiple,
    );

    this.display = el('span', { class: 'flex flex-wrap items-center gap-1.5 min-w-0 flex-1' });

    this.trigger = el('button', {
      class:
        'border-base-300 bg-base-100 rounded-field hover:border-base-content/25 flex w-full ' +
        'items-center gap-2 border px-3 py-2 text-left text-sm transition-colors min-h-10',
      attrs: { type: 'button', 'aria-haspopup': 'listbox', 'aria-expanded': 'false' },
    });
    if (config.id) this.trigger.id = config.id;
    this.trigger.append(this.display);
    this.trigger.append(
      el('span', { class: 'text-base-content/40 ml-auto shrink-0 text-xs', text: '▾' }),
    );

    config.root.replaceChildren(this.trigger);

    // The popover attaches itself to the trigger: no reference to keep.
    new Popover({
      trigger: this.trigger,
      build: (close) => this.buildPanel(close),
      onOpen: (panel) => $<HTMLInputElement>('input[type="search"]', panel)?.focus(),
    });

    this.renderTrigger();
  }

  get value(): string[] {
    return [...this.selected];
  }

  set value(ids: string[]) {
    this.selected = [...new Set(ids)];
    this.renderTrigger();
  }

  /** The single value, for simple mode. */
  get single(): string {
    return this.selected[0] ?? '';
  }

  private commit(): void {
    this.renderTrigger();
    this.config.onChange?.(this.value);
    // Feeds `trackDirtyForm`, which listens for the form's events.
    this.config.root.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private toggle(id: string): void {
    if (!this.config.multiple) {
      this.selected = [id];
      this.commit();
      return;
    }

    if (this.selected.includes(id)) {
      this.selected = this.selected.filter((value) => value !== id);
    } else {
      if (this.config.max && this.selected.length >= this.config.max) return;
      this.selected.push(id);
    }
    this.commit();
  }

  /** Colour swatch of a role, a flag, or `#` for a channel. */
  private optionMark(option: SelectOption): HTMLElement | null {
    if (option.iconUrl) {
      return el('img', {
        class: 'h-3.5 w-5 shrink-0 rounded-sm object-cover',
        attrs: { src: option.iconUrl, alt: '', loading: 'lazy' },
      });
    }
    if (option.hexColor) {
      return el('span', {
        class: 'size-2.5 shrink-0 rounded-full',
        style: { backgroundColor: option.hexColor },
      });
    }
    if (option.prefix === '#') {
      return el('span', { class: 'text-base-content/40 shrink-0', text: '#' });
    }
    return null;
  }

  private renderTrigger(): void {
    this.display.replaceChildren();

    if (this.selected.length === 0) {
      this.display.append(
        el('span', { class: 'text-base-content/40 truncate', text: this.config.placeholder }),
      );
      return;
    }

    // Single mode: one truncated label, never a line break.
    if (!this.config.multiple) {
      const option = this.byId.get(this.selected[0] ?? '');
      const label = el('span', { class: 'flex min-w-0 items-center gap-1.5' });

      if (option) {
        const mark = this.optionMark(option);
        if (mark) label.append(mark);
        label.append(el('span', { class: 'truncate', text: option.name }));
      } else {
        label.append(
          el('span', {
            class: 'truncate italic opacity-60',
            text: ct('selectionMissing', 'Selection not found'),
          }),
        );
      }

      this.display.append(label);
      return;
    }

    // Multiple mode: chips that wrap instead of scrolling.
    for (const id of this.selected) {
      const option = this.byId.get(id);
      const chip = el('span', {
        class: 'bg-base-300 rounded-field flex max-w-full items-center gap-1.5 py-0.5 pr-0.5 pl-2',
      });

      if (option) {
        const mark = this.optionMark(option);
        if (mark) chip.append(mark);
      }

      chip.append(
        el('span', {
          class: `truncate text-xs ${option ? '' : 'italic opacity-60'}`,
          text: option
            ? `${option.prefix ?? ''}${option.name}`
            : ct('unknownOption', 'Unknown ({id})', { id }),
        }),
      );

      if (option?.aboveBot) chip.append(aboveBotBadge('shrink-0 text-xs'));
      if (option?.writable === false) chip.append(aboveBotBadge('shrink-0 text-xs'));

      const remove = el('span', {
        class:
          'hover:bg-base-100 grid size-4 shrink-0 cursor-pointer place-items-center rounded text-xs',
        text: '✕',
        attrs: {
          role: 'button',
          'aria-label': ct('removeItem', 'Remove {name}', { name: option?.name ?? id }),
        },
      });
      remove.addEventListener('click', (event) => {
        event.stopPropagation();
        this.selected = this.selected.filter((value) => value !== id);
        this.commit();
      });
      chip.append(remove);

      this.display.append(chip);
    }
  }

  private buildPanel(close: () => void): HTMLElement {
    /*
     * Bounded by the panel, not by a figure of its own.
     *
     * It used to carry `max-h-80`. The panel now sizes itself to the room
     * under the trigger, and a wrapper insisting on three hundred and twenty
     * pixels inside a two-hundred-pixel panel is simply clipped — the last
     * rows of the list unreachable, with no scrollbar to say so.
     */
    /*
     * A flex child of the panel, allowed to shrink. `max-h-full` used to sit
     * here and resolved to nothing: the panel carries a maximum height, not a
     * height, and a percentage needs a definite one.
     */
    const wrapper = el('div', { class: 'flex min-h-0 flex-1 flex-col' });

    /*
     * `min-h-0`, and it is the whole of the fix.
     *
     * A flex child's minimum height is the height of its content, so a list of
     * forty roles simply made itself as tall as forty roles — the panel clipped
     * it, `overflow-y-auto` had nothing left to scroll, and the rows past the
     * fold were unreachable with no scrollbar to say so. The wrapper looked
     * right, the list looked right, and the two together did not.
     *
     * `flex-1` with it, so the list takes the room the search field leaves
     * rather than exactly its own content.
     */
    const list = el('div', { class: 'min-h-0 flex-1 overflow-y-auto p-1.5' });

    const renderList = () => {
      list.replaceChildren();

      const matches = this.config.options.filter((option) =>
        option.name.toLowerCase().includes(this.query),
      );

      if (matches.length === 0) {
        list.append(
          el('p', {
            class: 'text-base-content/50 px-3 py-6 text-center text-sm',
            text: ct('noResult', 'No result'),
          }),
        );
        return;
      }

      // "No selection", only where it is an answer the field accepts.
      if (!this.config.multiple && this.config.clearable && this.query === '') {
        list.append(this.buildRow({ id: '', name: this.config.emptyLabel }, close, true));
      }

      for (const option of matches) {
        list.append(this.buildRow(option, close, false));
      }
    };

    if (this.config.options.length > 8) {
      wrapper.append(
        searchField(this.config.searchPlaceholder, (query) => {
          this.query = query;
          renderList();
        }),
      );
    }

    renderList();
    wrapper.append(list);

    if (this.config.multiple && this.config.max) {
      wrapper.append(
        el('p', {
          class: 'border-base-300 text-base-content/45 border-t px-3 py-2 text-xs',
          text: `${String(this.selected.length)} / ${String(this.config.max)} ${ct('selectedCount', 'selected')}`,
        }),
      );
    }

    return wrapper;
  }

  private buildRow(option: SelectOption, close: () => void, isEmptyRow: boolean): HTMLElement {
    const isSelected = isEmptyRow ? this.selected.length === 0 : this.selected.includes(option.id);

    const row = el('div', {
      class:
        'rounded-field flex cursor-pointer items-center gap-2.5 px-2.5 py-2 text-sm transition-colors ' +
        (isSelected ? 'bg-primary/15 text-primary' : 'hover:bg-base-300'),
      attrs: { role: 'option', 'aria-selected': String(isSelected) },
    });

    // An explicit checkbox: this is what `<select multiple>` lacked most, its
    // selected state being invisible on a dark background.
    const box = el('span', {
      class:
        'grid size-4 shrink-0 place-items-center rounded border text-[0.625rem] ' +
        (isSelected ? 'bg-primary border-primary text-primary-content' : 'border-base-content/25'),
      text: isSelected ? '✓' : '',
    });
    row.append(box);

    const mark = this.optionMark(option);
    if (mark) row.append(mark);

    row.append(
      el('span', {
        class: `flex-1 truncate ${isEmptyRow ? 'italic opacity-60' : ''}`,
        text: option.name,
      }),
    );

    if (option.aboveBot) {
      row.append(
        el('span', {
          class: 'text-warning shrink-0 text-xs',
          text: '⚠',
          attrs: { title: ct('aboveBotShort', 'Above the bot in the hierarchy') },
        }),
      );
    }

    // `writable` is absent on every source but channels, and absent must not
    // read as "cannot write": only an explicit `false` marks one.
    if (option.writable === false) {
      row.append(
        el('span', {
          class: 'text-warning shrink-0 text-xs',
          text: '⚠',
          attrs: { title: ct('channelUnwritable', 'The bot cannot write here') },
        }),
      );
    }

    row.addEventListener('click', (event) => {
      event.stopPropagation();

      if (isEmptyRow) {
        this.selected = [];
        this.commit();
        close();
        return;
      }

      this.toggle(option.id);

      // Single mode: selecting closes the panel; in multiple mode we keep it
      // open so choices can be chained.
      if (!this.config.multiple) close();
      else row.replaceWith(this.buildRow(option, close, false));
    });

    return row;
  }
}

/**
 * Replaces the `<select>` elements marked `data-select-menu` with the component.
 *
 * We read the options from the original `<select>`: the server rendering stays
 * functional without JavaScript, and a hidden field preserves the value for
 * ordinary forms.
 */
/**
 * Created instances, findable by their host.
 * The pages that used to read `select.value` must be able to query the component.
 */
const menusByHost = new WeakMap<HTMLElement, SelectMenu>();

/**
 * Finds the component that replaced a field.
 *
 * Matched on `data-select-host`, which `upgradeSelects` always sets from the
 * `name` of the original `<select>`. It used to match `data-field` instead - an
 * attribute only the Logging page puts on its tags. Every other page therefore
 * got `null` back and saved the fallback value: eleven multi-selects across the
 * Levels, Moderation, AutoMod and Welcome pages silently stored nothing at all.
 *
 * `data-field` is still honoured, because Logging addresses its rows by a name
 * that is not the field's.
 */
export function findSelectMenu(root: ParentNode, field: string): SelectMenu | null {
  const escaped = CSS.escape(field);

  const host =
    root.querySelector<HTMLElement>(`[data-select-host="${escaped}"]`) ??
    root.querySelector<HTMLElement>(`[data-select-host][data-field="${escaped}"]`);

  return host ? (menusByHost.get(host) ?? null) : null;
}

/**
 * Option lists shared by several menus on one page.
 *
 * The AutoMod page offers the same roles and the same channels to eleven rules,
 * twice each. Written out as `<option>` tags that is the whole list twenty-two
 * times: on a server with 120 roles and 200 channels the page reached half a
 * megabyte, and the browser spent the wait parsing lists nobody had opened.
 *
 * A menu carrying `data-select-source` reads its options from a JSON block
 * instead, published once. The `<select>` then holds only what is *selected* -
 * which is the part that genuinely differs from one rule to the next.
 */
function sharedSources(root: ParentNode): Map<string, SelectOption[]> {
  const node = root.querySelector?.('#select-sources');
  if (!node?.textContent) return new Map();

  try {
    const parsed = JSON.parse(node.textContent) as Record<string, SelectOption[]>;
    return new Map(Object.entries(parsed));
  } catch {
    // A malformed block must not take the page's JavaScript down with it: the
    // menus fall back to whatever `<option>` tags they carry.
    return new Map();
  }
}

export function upgradeSelects(root: ParentNode = document): SelectMenu[] {
  const menus: SelectMenu[] = [];
  const sources = sharedSources(root);

  for (const select of root.querySelectorAll<HTMLSelectElement>('select[data-select-menu]')) {
    const multiple = select.multiple;

    const shared = select.dataset.selectSource
      ? sources.get(select.dataset.selectSource)
      : undefined;

    /*
     * The empty entry the page wrote, which says two things at once: that
     * nothing is a valid answer here, and what that answer is called — "the
     * same channel" reads better than a generic "no selection".
     */
    const emptyOption = [...select.options].find((option) => option.value === '');

    const options: SelectOption[] =
      shared?.map((option) => ({ ...option, prefix: select.dataset.prefix ?? option.prefix })) ??
      [...select.options]
        .filter((option) => option.value !== '')
        .map((option) => ({
          id: option.value,
          name: option.textContent?.trim().replace(/^[#@]/, '') ?? option.value,
          hexColor: option.dataset.color || null,
          iconUrl: option.dataset.icon || null,
          aboveBot: option.dataset.aboveBot === 'true',
          prefix: select.dataset.prefix ?? '',
        }));

    /*
     * A shared menu carries its selection in `data-selected` rather than in
     * `selected` attributes, since it has no `<option>` tags to put them on.
     */
    const initial = shared
      ? (select.dataset.selected ?? '').split(',').filter(Boolean)
      : [...select.selectedOptions].map((option) => option.value).filter(Boolean);

    const host = el('div', { class: 'w-full' });
    host.dataset.selectHost = select.name || select.id;

    // The `data-*` attributes of the original tag are copied over: the pages
    // target their fields by `data-field`, and losing that attribute would
    // silently break reading the values at save time.
    for (const [key, value] of Object.entries(select.dataset)) {
      if (key !== 'selectMenu' && value !== undefined) host.dataset[key] = value;
    }

    select.replaceWith(host);

    // Hidden field: preserves the value for `formToObject` and native submissions.
    const hidden = el('input', { attrs: { type: 'hidden', name: select.name } });
    hidden.value = multiple ? initial.join(',') : (initial[0] ?? '');
    host.append(hidden);

    const menu = new SelectMenu({
      root: host,
      id: select.id || undefined,
      options,
      initial,
      multiple,
      placeholder: select.dataset.placeholder ?? ct('selectPlaceholder', 'Select…'),
      searchPlaceholder: select.dataset.searchPlaceholder ?? ct('searchPlaceholder', 'Search…'),
      emptyLabel:
        select.dataset.emptyLabel ??
        emptyOption?.textContent?.trim() ??
        ct('noSelection', 'No selection'),
      /*
       * A menu built from a shared list has no tags to read, so it keeps the
       * old behaviour unless the page says otherwise: those are the channel and
       * role pickers, where choosing nothing is nearly always meaningful.
       */
      clearable:
        select.dataset.clearable !== undefined
          ? select.dataset.clearable !== 'false'
          : Boolean(emptyOption) || Boolean(select.dataset.selectSource),
      max: select.dataset.max ? Number(select.dataset.max) : undefined,
      onChange: (selected) => {
        hidden.value = multiple ? selected.join(',') : (selected[0] ?? '');
      },
    });

    menusByHost.set(host, menu);
    menus.push(menu);

    // The hidden field is added after the trigger by `replaceChildren`: we
    // reinsert it so it survives the component's rendering.
    host.append(hidden);
  }

  return menus;
}
